import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Check, Eye, EyeOff, ExternalLink, Tv, QrCode, MessageCircle, FilePenLine, RotateCcw, Save, Image as ImageIcon, FileText } from "lucide-react";
import { COMUNICACION } from "@/lib/experienciaContent";

const PROPERTY_LABEL = { casita: "La Casita", loft: "El Loft" };

export default function GuestPortalManager({ token }) {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    guest_name: "",
    property_id: "casita",
    check_in: "",
    check_out: "",
    guests: "",
    notes: "",
    has_pets: false,
  });
  const [copiedId, setCopiedId] = useState(null);
  const [messagesFor, setMessagesFor] = useState(null); // portal being previewed
  const [copiedStepIdx, setCopiedStepIdx] = useState(null);
  const [templates, setTemplates] = useState(null); // merged templates from backend
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp-templates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(data.templates || []);
    } catch {
      // silent fallback to defaults from experienciaContent
      setTemplates(COMUNICACION.steps.map((s) => ({ ...s, is_customized: false })));
    }
  }, [token]);

  const fetchPortals = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/guest-portal", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPortals(data.portals || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPortals();
    fetchTemplates();
  }, [fetchPortals, fetchTemplates]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guest_name.trim()) {
      toast.error("El nombre del huésped es obligatorio");
      return;
    }
    try {
      const payload = { ...form };
      if (payload.guests) payload.guests = parseInt(payload.guests, 10);
      else delete payload.guests;
      if (!payload.check_in) delete payload.check_in;
      if (!payload.check_out) delete payload.check_out;
      if (!payload.notes) delete payload.notes;
      payload.has_pets = !!payload.has_pets;

      await api.post("/admin/guest-portal", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Portal creado");
      setShowForm(false);
      setForm({ guest_name: "", property_id: "casita", check_in: "", check_out: "", guests: "", notes: "", has_pets: false });
      fetchPortals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el portal");
    }
  };

  const toggleActive = async (portal) => {
    try {
      await api.patch(
        `/admin/guest-portal/${portal.id}`,
        { active: !portal.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(portal.active ? "Portal desactivado" : "Portal activado");
      fetchPortals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const remove = async (portal) => {
    if (!window.confirm(`¿Eliminar el acceso de ${portal.guest_name}? No se podrá recuperar.`)) return;
    try {
      await api.delete(`/admin/guest-portal/${portal.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Portal eliminado");
      fetchPortals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const portalUrl = (token) => `${window.location.origin}/portal/${token}`;
  const bienvenidaUrl = (token) => `${window.location.origin}/portal/${token}/bienvenida`;

  const downloadQR = async (portal) => {
    try {
      const res = await api.get(`/admin/guest-portal/${portal.id}/qr.png`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "image/png" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `QR-${portal.guest_name.replace(/\s+/g, "-")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("QR descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descargar el QR");
    }
  };

  const downloadTicket = async (portal, kind) => {
    // kind = "png" | "pdf"
    try {
      const res = await api.get(`/admin/guest-portal/${portal.id}/ticket.${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const mime = kind === "pdf" ? "application/pdf" : "image/png";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pase-${portal.guest_name.replace(/\s+/g, "-").toLowerCase()}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Pase ${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo descargar el pase ${kind.toUpperCase()}`);
    }
  };

  const copyLink = async (portal) => {
    try {
      await navigator.clipboard.writeText(portalUrl(portal.token));
      setCopiedId(portal.id);
      toast.success("Enlace copiado");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  // Render each step with the guest's first name AND portal link substituted.
  // Prefers the (possibly customized) templates from backend, falls back to defaults.
  const renderedMessages = useMemo(() => {
    if (!messagesFor) return [];
    const firstName = (messagesFor.guest_name || "").split(" ")[0] || "familia";
    const link = messagesFor.token ? portalUrl(messagesFor.token) : "";
    const source = templates && templates.length > 0 ? templates : COMUNICACION.steps;
    return source.map((step) => ({
      key: step.key,
      moment: step.moment,
      label: step.label,
      body: step.body,
      is_customized: step.is_customized,
      text: (step.message || [])
        .map((l) => l.replaceAll("{Nombre}", firstName).replaceAll("{PortalLink}", link))
        .join("\n\n"),
    }));
  }, [messagesFor, templates]);

  const copyMessage = async (idx, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStepIdx(idx);
      toast.success("Mensaje copiado");
      setTimeout(() => setCopiedStepIdx(null), 2000);
    } catch {
      toast.error("No se pudo copiar el mensaje");
    }
  };

  const openWhatsApp = (text) => {
    // No guest phone stored — open wa.me with just prefilled text so owner
    // pastes/forwards from their existing chat with the guest
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (loading) {
    return <p className="text-stone-500">Cargando accesos…</p>;
  }

  return (
    <div data-testid="guest-portal-manager" className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif-display text-3xl md:text-4xl leading-tight text-stone-900">
            Portal del huésped
          </h2>
          <p className="text-stone-600 mt-2 max-w-2xl">
            Crea enlaces privados para cada reserva. Cada huésped recibe una experiencia digital personalizada.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setShowTemplateEditor(true)}
            data-testid="portal-edit-templates-btn"
            variant="outline"
            className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white rounded-none px-5 py-3 text-sm uppercase tracking-[0.18em] font-semibold"
          >
            <FilePenLine className="w-4 h-4 mr-2" />
            Editar plantillas
          </Button>
          <Button
            onClick={() => setShowForm((s) => !s)}
            data-testid="portal-new-btn"
            className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none px-6 py-3 text-sm uppercase tracking-[0.18em] font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? "Cancelar" : "Nuevo acceso"}
          </Button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          data-testid="portal-new-form"
          className="bg-white border border-stone-200 p-6 md:p-8 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="guest_name" className="text-eyebrow text-stone-600 mb-2 block">
                Nombre del huésped *
              </Label>
              <Input
                id="guest_name"
                data-testid="portal-form-name"
                value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                placeholder="Ana García"
                required
                className="rounded-none"
              />
            </div>
            <div>
              <Label className="text-eyebrow text-stone-600 mb-2 block">Alojamiento *</Label>
              <div className="flex gap-2">
                {["casita", "loft"].map((pid) => (
                  <button
                    key={pid}
                    type="button"
                    onClick={() => setForm({ ...form, property_id: pid })}
                    data-testid={`portal-form-property-${pid}`}
                    className={`flex-1 py-2.5 text-sm uppercase tracking-[0.15em] font-semibold border transition-all ${
                      form.property_id === pid
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                    }`}
                  >
                    {PROPERTY_LABEL[pid]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="check_in" className="text-eyebrow text-stone-600 mb-2 block">
                Check-in
              </Label>
              <Input
                id="check_in"
                type="date"
                data-testid="portal-form-checkin"
                value={form.check_in}
                onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                className="rounded-none"
              />
            </div>
            <div>
              <Label htmlFor="check_out" className="text-eyebrow text-stone-600 mb-2 block">
                Check-out
              </Label>
              <Input
                id="check_out"
                type="date"
                data-testid="portal-form-checkout"
                value={form.check_out}
                onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                className="rounded-none"
              />
            </div>
            <div>
              <Label htmlFor="guests" className="text-eyebrow text-stone-600 mb-2 block">
                Huéspedes
              </Label>
              <Input
                id="guests"
                type="number"
                min="1"
                data-testid="portal-form-guests"
                value={form.guests}
                onChange={(e) => setForm({ ...form, guests: e.target.value })}
                placeholder="6"
                className="rounded-none"
              />
            </div>
            <div>
              <Label htmlFor="notes" className="text-eyebrow text-stone-600 mb-2 block">
                Notas internas
              </Label>
              <Input
                id="notes"
                data-testid="portal-form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Cumpleaños, alergias, etc."
                className="rounded-none"
              />
            </div>
            <div className="md:col-span-2">
              <label
                className={`flex items-start gap-3 border p-4 cursor-pointer transition-all ${
                  form.has_pets
                    ? "border-[#B84A2C] bg-[#FBEEEA]"
                    : "border-stone-300 bg-white hover:border-stone-500"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.has_pets}
                  onChange={(e) => setForm({ ...form, has_pets: e.target.checked })}
                  data-testid="portal-form-has-pets"
                  className="mt-1 w-5 h-5 accent-[#B84A2C]"
                />
                <div className="flex-1">
                  <p className="font-semibold text-stone-900 flex items-center gap-2">
                    <i className="fa-solid fa-paw text-[#B84A2C]" />
                    Este huésped lleva mascota(s)
                  </p>
                  <p className="text-xs text-stone-600 mt-1 leading-relaxed">
                    Activa una sección roja obligatoria en el portal con el manifiesto de mascotas.
                    Si no lo acepta 1 día antes del check-in, te llegará un email de alerta.
                  </p>
                </div>
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              data-testid="portal-form-submit"
              className="bg-stone-900 hover:bg-stone-800 text-white rounded-none px-6 py-3 text-sm uppercase tracking-[0.18em] font-semibold"
            >
              Generar enlace
            </Button>
          </div>
        </form>
      )}

      {/* List */}
      {portals.length === 0 ? (
        <div className="bg-white border border-stone-200 p-12 text-center">
          <p className="text-stone-500">No hay accesos creados aún.</p>
          <p className="text-stone-400 text-sm mt-2">
            Crea un nuevo acceso para generar un enlace privado personalizado.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {portals.map((p) => (
            <div
              key={p.id}
              data-testid={`portal-row-${p.id}`}
              className={`bg-white border p-5 md:p-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-start ${
                p.active ? "border-stone-200" : "border-stone-200 bg-stone-50 opacity-70"
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-baseline gap-3 flex-wrap mb-1">
                  <h3 className="font-serif-display text-2xl text-stone-900">{p.guest_name}</h3>
                  <span className="text-eyebrow text-[#E07A5F]">{PROPERTY_LABEL[p.property_id]}</span>
                  {p.has_pets && (
                    <span
                      data-testid={`portal-pet-badge-${p.id}`}
                      className={`text-[10px] uppercase tracking-[0.24em] px-2 py-1 flex items-center gap-1.5 ${
                        p.pets_accepted
                          ? "bg-[#E4EDE7] text-[#3B6B4F]"
                          : "bg-[#FBEEEA] text-[#7A2E17]"
                      }`}
                      title={p.pets_accepted ? "Manifiesto aceptado por el huésped" : "Pendiente: el huésped aún no acepta el manifiesto"}
                    >
                      <i className="fa-solid fa-paw" />
                      {p.pets_accepted ? "Mascotas · Aceptado" : "Mascotas · Pendiente"}
                    </span>
                  )}
                  {!p.active && (
                    <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500 bg-stone-200 px-2 py-1">
                      Desactivado
                    </span>
                  )}
                </div>
                <div className="text-sm text-stone-600 flex gap-4 flex-wrap mt-2">
                  {p.check_in && <span>Check-in: <strong>{p.check_in}</strong></span>}
                  {p.check_out && <span>Check-out: <strong>{p.check_out}</strong></span>}
                  {p.guests && <span>Huéspedes: <strong>{p.guests}</strong></span>}
                  <span>Vistas: <strong>{p.views || 0}</strong></span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <code className="text-xs bg-stone-100 px-3 py-2 text-stone-700 truncate flex-1 min-w-0">
                    {portalUrl(p.token)}
                  </code>
                </div>
                {p.notes && (
                  <p className="text-xs text-stone-500 mt-2 italic">Notas: {p.notes}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 lg:flex-nowrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyLink(p)}
                  data-testid={`portal-copy-${p.id}`}
                  className="rounded-none border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white"
                >
                  {copiedId === p.id ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  Copiar
                </Button>
                <a
                  href={portalUrl(p.token)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`portal-open-${p.id}`}
                  className="inline-flex items-center border border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white px-3 py-2 text-sm transition-all"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir
                </a>
                <a
                  href={bienvenidaUrl(p.token)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`portal-tv-${p.id}`}
                  className="inline-flex items-center border border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white px-3 py-2 text-sm transition-all"
                  title="Abrir pantalla de bienvenida para TV"
                >
                  <Tv className="w-4 h-4 mr-2" />
                  TV
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadQR(p)}
                  data-testid={`portal-qr-${p.id}`}
                  className="rounded-none border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white"
                  title="Descargar QR (para imprimir/enviar por WhatsApp)"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  QR
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadTicket(p, "png")}
                  data-testid={`portal-ticket-png-${p.id}`}
                  className="rounded-none border-[#E07A5F] text-[#B85D42] hover:bg-[#E07A5F] hover:text-white"
                  title="Pase digital estilo boleto — imagen PNG para enviar por WhatsApp"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Pase PNG
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadTicket(p, "pdf")}
                  data-testid={`portal-ticket-pdf-${p.id}`}
                  className="rounded-none border-[#E07A5F] text-[#B85D42] hover:bg-[#E07A5F] hover:text-white"
                  title="Pase en PDF (US Letter) — imprimible en casa"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Pase PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMessagesFor(p)}
                  data-testid={`portal-messages-${p.id}`}
                  className="rounded-none border-[#25D366] text-[#1E8E4A] hover:bg-[#25D366] hover:text-white"
                  title="Ver y copiar los 7 mensajes de WhatsApp para este huésped"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Mensajes
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleActive(p)}
                  data-testid={`portal-toggle-${p.id}`}
                  className="rounded-none border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white"
                >
                  {p.active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {p.active ? "Desactivar" : "Activar"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(p)}
                  data-testid={`portal-delete-${p.id}`}
                  className="rounded-none border-red-300 text-red-700 hover:bg-red-600 hover:text-white"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mensajes WhatsApp Dialog */}
      <Dialog open={!!messagesFor} onOpenChange={(o) => !o && setMessagesFor(null)}>
        <DialogContent
          data-testid="portal-messages-dialog"
          className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-none"
        >
          <DialogHeader className="p-6 md:p-8 border-b border-stone-200 sticky top-0 bg-white z-10">
            <DialogTitle className="font-serif-display text-3xl text-stone-900">
              Mensajes WhatsApp — {messagesFor?.guest_name}
            </DialogTitle>
            <DialogDescription className="text-stone-600 text-[15px] leading-relaxed">
              Los 7 mensajes oficiales, personalizados con el nombre del huésped. Copia el que
              corresponda al momento del viaje y envíaselo desde tu WhatsApp.
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 md:p-8 space-y-6">
            {renderedMessages.map((m, idx) => (
              <div
                key={idx}
                data-testid={`portal-messages-step-${idx + 1}`}
                className="border border-stone-200 bg-white"
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap px-5 py-4 border-b border-stone-200 bg-stone-50">
                  <div>
                    <p className="text-[10px] tracking-[0.28em] uppercase text-stone-500">
                      Mensaje {idx + 1} · {m.moment}
                      {m.is_customized && (
                        <span
                          data-testid={`portal-messages-customized-${idx + 1}`}
                          className="ml-2 inline-block px-1.5 py-0.5 bg-amber-100 text-amber-900 text-[9px] tracking-[0.18em] normal-case font-semibold rounded-sm"
                          title="Este mensaje fue personalizado por el admin"
                        >
                          editado
                        </span>
                      )}
                    </p>
                    <h4 className="font-serif-display text-xl text-stone-900 mt-1">{m.label}</h4>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyMessage(idx, m.text)}
                      data-testid={`portal-messages-copy-${idx + 1}`}
                      className="rounded-none border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white"
                    >
                      {copiedStepIdx === idx ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copiedStepIdx === idx ? "Copiado" : "Copiar"}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openWhatsApp(m.text)}
                      data-testid={`portal-messages-wa-${idx + 1}`}
                      className="rounded-none bg-[#25D366] hover:bg-[#1DA851] text-white"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </div>
                <div className="p-5 bg-[#E8F5E1] border-l-4 border-[#25D366]">
                  <p className="text-stone-800 leading-relaxed whitespace-pre-line text-[14.5px]">
                    {m.text}
                  </p>
                  {m.key === "bienvenida_portal" && (
                    <div
                      className="mt-4 pt-4 border-t border-[#B5D8A3] flex flex-wrap gap-2 items-center"
                      data-testid={`portal-messages-pass-${idx + 1}`}
                    >
                      <span className="text-[10px] tracking-[0.24em] uppercase text-[#2C5C1F] font-semibold mr-2">
                        Adjuntar pase digital →
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadTicket(messagesFor, "png")}
                        data-testid={`portal-messages-pass-png-${idx + 1}`}
                        className="rounded-none border-[#E07A5F] text-[#B85D42] hover:bg-[#E07A5F] hover:text-white bg-white"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Pase PNG
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadTicket(messagesFor, "pdf")}
                        data-testid={`portal-messages-pass-pdf-${idx + 1}`}
                        className="rounded-none border-[#E07A5F] text-[#B85D42] hover:bg-[#E07A5F] hover:text-white bg-white"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Pase PDF
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Editor Dialog — edit the 7 master WhatsApp templates */}
      <TemplateEditorDialog
        open={showTemplateEditor}
        onClose={() => setShowTemplateEditor(false)}
        templates={templates || []}
        token={token}
        onSaved={fetchTemplates}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------
 * TemplateEditorDialog — edits the 7 master WhatsApp message templates.
 * Each row shows a textarea (message paragraphs separated by blank lines)
 * plus Save + Reset-to-default per template.
 * ------------------------------------------------------------------------- */
function TemplateEditorDialog({ open, onClose, templates, token, onSaved }) {
  const [drafts, setDrafts] = useState({}); // { key: { message, label, body } }
  const [savingKey, setSavingKey] = useState(null);
  const [resetingKey, setResetingKey] = useState(null);

  // Whenever the dialog opens or templates change, seed the drafts.
  useEffect(() => {
    if (!open) return;
    const seed = {};
    for (const t of templates) {
      seed[t.key] = {
        label: t.label || "",
        body: t.body || "",
        // textarea uses \n\n between paragraphs (matches how the message renders)
        message_text: (t.message || []).join("\n\n"),
      };
    }
    setDrafts(seed);
  }, [open, templates]);

  const updateDraft = (key, patch) => {
    setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));
  };

  const saveOne = async (key) => {
    const draft = drafts[key];
    if (!draft) return;
    // Split message_text by blank lines (\n\n or more) — keep non-empty lines
    const message = draft.message_text
      .split(/\n{2,}/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (message.length === 0) {
      toast.error("El mensaje no puede estar vacío");
      return;
    }
    setSavingKey(key);
    try {
      await api.put(
        `/admin/whatsapp-templates/${key}`,
        { message, label: draft.label, body: draft.body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Plantilla guardada");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la plantilla");
    } finally {
      setSavingKey(null);
    }
  };

  const resetOne = async (key) => {
    if (!window.confirm("¿Restaurar el mensaje al texto original de fábrica?")) return;
    setResetingKey(key);
    try {
      await api.delete(`/admin/whatsapp-templates/${key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Plantilla restaurada al original");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar");
    } finally {
      setResetingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="whatsapp-templates-editor"
        className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 gap-0 rounded-none"
      >
        <DialogHeader className="p-6 md:p-8 border-b border-stone-200 sticky top-0 bg-white z-10">
          <DialogTitle className="font-serif-display text-3xl text-stone-900">
            Plantillas de WhatsApp
          </DialogTitle>
          <DialogDescription className="text-stone-600 text-[15px] leading-relaxed">
            Edita el texto de los mensajes oficiales. Los cambios se aplican de inmediato a la
            vista de &quot;Mensajes&quot; de cada huésped. Placeholders disponibles: <code className="px-1 bg-stone-100 rounded-sm">{"{Nombre}"}</code> (primer nombre del huésped) · <code className="px-1 bg-stone-100 rounded-sm">{"{PortalLink}"}</code> (enlace de su portal privado). Separa párrafos con <em>una línea en blanco</em>.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 md:p-8 space-y-8">
          {templates.map((t) => {
            const draft = drafts[t.key] || { label: "", body: "", message_text: "" };
            return (
              <div
                key={t.key}
                data-testid={`whatsapp-template-editor-${t.key}`}
                className={`border ${t.is_customized ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap px-5 py-4 border-b border-stone-200 bg-stone-50">
                  <div>
                    <p className="text-[10px] tracking-[0.28em] uppercase text-stone-500">
                      {t.moment}
                      {t.is_customized && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 bg-amber-100 text-amber-900 text-[9px] tracking-[0.18em] normal-case font-semibold rounded-sm">
                          editado
                        </span>
                      )}
                    </p>
                    <h4 className="font-serif-display text-xl text-stone-900 mt-1">{draft.label || t.label}</h4>
                  </div>
                  <div className="flex gap-2">
                    {t.is_customized && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetOne(t.key)}
                        disabled={resetingKey === t.key}
                        data-testid={`whatsapp-template-reset-${t.key}`}
                        className="rounded-none border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        {resetingKey === t.key ? "Restaurando…" : "Restaurar default"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => saveOne(t.key)}
                      disabled={savingKey === t.key}
                      data-testid={`whatsapp-template-save-${t.key}`}
                      className="rounded-none bg-stone-900 hover:bg-stone-700 text-white"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingKey === t.key ? "Guardando…" : "Guardar"}
                    </Button>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  <div>
                    <Label className="text-eyebrow text-stone-600 mb-2 block">
                      Título del paso (privado — solo tú lo ves)
                    </Label>
                    <Input
                      value={draft.label}
                      onChange={(e) => updateDraft(t.key, { label: e.target.value })}
                      data-testid={`whatsapp-template-label-${t.key}`}
                      className="rounded-none"
                    />
                  </div>

                  <div>
                    <Label className="text-eyebrow text-stone-600 mb-2 block">
                      Descripción corta (visible al huésped en el timeline del portal)
                    </Label>
                    <textarea
                      value={draft.body}
                      onChange={(e) => updateDraft(t.key, { body: e.target.value })}
                      data-testid={`whatsapp-template-body-${t.key}`}
                      rows={2}
                      className="w-full rounded-none border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-stone-900"
                    />
                  </div>

                  <div>
                    <Label className="text-eyebrow text-stone-600 mb-2 block">
                      Texto del mensaje ({"{Nombre}"} → nombre del huésped · {"{PortalLink}"} → enlace del portal · línea en blanco = nuevo párrafo)
                    </Label>
                    <textarea
                      value={draft.message_text}
                      onChange={(e) => updateDraft(t.key, { message_text: e.target.value })}
                      data-testid={`whatsapp-template-message-${t.key}`}
                      rows={9}
                      className="w-full rounded-none border border-stone-300 px-3 py-2 text-[14.5px] leading-relaxed font-mono focus:outline-none focus:border-stone-900"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
